import { useFormik } from 'formik';
import { initialValues, validationSchema } from './formData';
import { ISelect, ParamEnum } from '../../../../../../shared/types/shared';
import { useEffect, useState } from 'react';
import { PqrDataSfc } from '../../../../../../shared/types/modules/pqr';
import { PqrDataSfcProps } from './props';
import {
    asyncGetParamById,
    asyncGetParams,
} from '../../../../../../services/param.service';

export const usePqrDataSfc = ({
    data,
    onChangeData,
    onChangeFormik,
    receptionChannel,
    half,
    type,
}: PqrDataSfcProps) => {
    const [channelPresentsFailures, setChannelPresentsFailures] = useState<
        ISelect[]
    >([]);
    const [controlEntities, setControlEntities] = useState<ISelect[]>([]);
    const [receptionInstances, setReceptionInstances] = useState<ISelect[]>([]);
    const [receptionPoints, setReceptionPoints] = useState<ISelect[]>([]);
    const [admissions, setAdmissions] = useState<ISelect[]>([]);
    const [isAdmission, setIsAdmission] = useState<boolean>(false);
    const [isRequest, setIsRequest] = useState<boolean>(false);
    const [isSfc, setIsSfc] = useState<boolean>(false);
    const onSubmit = (data: PqrDataSfc) => {
        if (onChangeData) onChangeData(data || {});
    };

    const formik = useFormik({
        initialValues,
        validationSchema,
        onSubmit,
    });

    useEffect(() => {
        if (onChangeFormik) onChangeFormik(formik);
    }, [formik.values, formik.errors]);

    useEffect(() => {
        formik?.setFieldValue(
            'channelPresentsFailure',
            data?.channelPresentsFailure || ''
        );
        formik?.setFieldValue('controlEntity', data?.controlEntity || '');
        formik?.setFieldValue(
            'receptionInstance',
            data?.receptionInstance || ''
        );
        formik?.setFieldValue('receptionPoint', data?.receptionPoint || '');
        formik?.setFieldValue('admission', data?.admission || '');
        formik?.setFieldValue(
            'hasExpressComplaint',
            data?.hasExpressComplaint || ''
        );
    }, [data]);

    useEffect(() => {
        asyncGetParams(ParamEnum.PQR_CHANNEL_PRESENTS_FAILURE).then((items) =>
            setChannelPresentsFailures(items)
        );
        asyncGetParams(ParamEnum.PQR_CONTROL_ENTITY).then((items) =>
            setControlEntities(items)
        );
        asyncGetParams(ParamEnum.PQR_RECEPTION_INSTANCE).then((items) =>
            setReceptionInstances(items)
        );
    }, []);

    useEffect(() => {
        if (Boolean(receptionChannel?.trim()))
            asyncGetParams(
                ParamEnum.PQR_RECEPTION_POINT,
                undefined,
                receptionChannel
            ).then((items) => {
                setReceptionPoints(items);
                formik?.setFieldValue('receptionPoint', items?.[0]?.id || '');
            });
        else {
            setReceptionPoints([]);
            formik?.setFieldValue('receptionPoint', '');
        }
    }, [receptionChannel]);

    const filterParamsByHalf = (items: ISelect[], half: string) => {
        return (
            items?.filter((param: ISelect) => param?.parent?.id === half) || []
        );
    };

    const asyncNotApplicableParam = (items: ISelect[]) => {
        return (
            items?.filter(
                (param: ISelect) =>
                    param?.code === `${ParamEnum.PQR_ADMISSION}_005`
            ) || []
        );
    };

    useEffect(() => {
        if (Boolean(half?.trim())) {
            asyncGetParams(ParamEnum.PQR_ADMISSION).then((items) => {
                const filteredParams = filterParamsByHalf(items, half);

                if (filteredParams.length > 0) {
                    setAdmissions(filteredParams);
                    setIsAdmission(false);
                    formik?.setFieldValue('admission', '');
                } else {
                    const notApplicableParam = asyncNotApplicableParam(items);
                    setAdmissions(notApplicableParam);
                    setIsAdmission(true);
                    formik?.setFieldValue(
                        'admission',
                        notApplicableParam[0]?.id || ''
                    );
                }
            });

            asyncGetParamById(half).then((item) => {
                setIsSfc(item?.code === `${ParamEnum.PQR_HALF}_003`);
            });
        }
    }, [half]);

    useEffect(() => {
        if (Boolean(type?.trim())) {
            asyncGetParamById(type).then((item) => {
                setIsRequest(item?.code === `${ParamEnum.PQR_TYPE}_PTC`);
            });
        }
    }, [type]);

    useEffect(() => {
        if (isRequest && isSfc) {
            formik?.setFieldValue(
                'receptionInstance',
                receptionInstances.filter(
                    (item) =>
                        item.code === `${ParamEnum.PQR_RECEPTION_INSTANCE}_SFC`
                )[0]?.id || ''
            );
        } else {
            formik?.setFieldValue('receptionInstance', '');
        }
    }, [isRequest, isSfc]);

    return {
        formik,
        channelPresentsFailures,
        controlEntities,
        receptionInstances,
        receptionPoints,
        admissions,
        isAdmission,
        isRequest,
        isSfc,
    };
};
